// Shared recruiter+candidate components call t(). Register i18n before any
// page module evaluates so those calls resolve to English instead of raw keys.
import './i18n';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router-dom';
import { Box, MantineProvider, Stack } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
// The only app that renders charts. In the app shell's stylesheet, so it loads
// before any page and precedes page CSS; no shell or page CSS targets chart
// classes, so its place after the entry stylesheet changes no rule.
import '@mantine/charts/styles.css';
import { BulkOperationProvider } from 'context-providers/BulkOperationContext';
import { FeatureErrorBoundary } from 'components/FeatureErrorBoundary';
import { isBackendOverrideAllowed } from 'env';
import {
  hydrateSessionFromCache,
  startSessionVerification,
} from 'stores/sessionStore';

import { createAppRouter } from './app-shell/createAppRouter';
import { isProtectedPagePath } from './app-shell/preloadRouteChunk';
import { ensureEnglishCommon } from './i18n';
import { useVisibilityRefresh } from './hooks/useVisibilityRefresh';
import { bootRecruiterI18n } from './recruiter-i18n';
import { checkLocalServerRunning } from './serverCheck';
import { useSessionStorageSync } from './hooks/useSessionStorageSync';
import { bootRecruiterSession } from './app-shell/recruiterSessionBoot';
import { lazyWithReload } from './utils/lazyWithReload';
import { numberInputSeparators } from './utils/appLocale';
import { handleNumberInputPaste } from './utils/numberInputPaste';
import { buildAppTheme } from './appTheme';
import { RecruiterLanguageRecovery } from './recruiter-i18n/RecruiterLanguageRecovery';
import appShell from './App.module.css';

// A dev-only control, never shown in production, so it loads on demand.
const BackendSelector = lazyWithReload(
  () => import('components/dev/BackendSelector')
);

// Applies the cached display language (the entry holds the first render on
// it), lets session verification apply the server's value before the session
// is marked ready, and loads the staff strings for staff sessions (it follows
// the support store that bootRecruiterSession hydrates). Registered before the
// verify response is applied, which is when the applier is read.
bootRecruiterI18n();

// Hydrates the support store and starts the verify request. The entry normally
// ran it already, while this chunk was downloading; then this call is a no-op.
bootRecruiterSession();

// A protected page renders from the cache of the last verified session until
// verify returns. Before the first render, since components read identity and
// permissions without subscribing; the first render waits for this chunk anyway.
if (isProtectedPagePath(window.location.pathname)) {
  hydrateSessionFromCache();
}

// Consumes the verify request bootRecruiterSession started. The entry also starts
// the route's chunks (startRecruiterRouteWarmup): ProtectedRoute renders nothing
// until verify returns unless a cached session lets it render early, and
// React.lazy alone would not request a chunk until it renders.
void startSessionVerification();
// Registers English `common`, which every page waits on (createAppRouter); the
// entry's warm-up has the module in flight. A failure is retried by the page's
// own load.
// swallow-ok: Route loaders await ensureEnglishCommon again; a failed warmup evicts its promise and the route error boundary owns retry.
ensureEnglishCommon().catch(() => {});

const router = createAppRouter();

const App = () => {
  // Recompute input defaults when a retry or session changes the UI language.
  useTranslation();
  useVisibilityRefresh();
  useEffect(() => {
    void checkLocalServerRunning();
  }, []);

  useSessionStorageSync();
  const decimalSeparator = numberInputSeparators().decimalSeparator;
  const theme = useMemo(
    () =>
      buildAppTheme({
        decimalSeparator,
        numberInputExtras: { onPaste: handleNumberInputPaste },
        readOnlyControls: 'all',
      }),
    [decimalSeparator]
  );

  return (
    <MantineProvider theme={theme}>
      <ModalsProvider>
        <BulkOperationProvider>
          <Notifications
            position='top-right'
            onClick={(e) => e.nativeEvent.stopImmediatePropagation()}
          />
          {isBackendOverrideAllowed() && (
            <FeatureErrorBoundary feature='backend_selector' variant='silent'>
              <React.Suspense fallback={null}>
                <BackendSelector />
              </React.Suspense>
            </FeatureErrorBoundary>
          )}
          <Stack h='100%' gap={0}>
            <RecruiterLanguageRecovery />
            <Box flex={1} mih={0} className={appShell.routerViewport}>
              <RouterProvider router={router} />
            </Box>
          </Stack>
        </BulkOperationProvider>
      </ModalsProvider>
    </MantineProvider>
  );
};

export default App;
